/**
 * Bob Laadt Op — bob-day-flow: de dag in beeld ("Hoe werkt een plug-in thuisbatterij?").
 * Bron: _design/Bob-laadt-op-thomas/werkbestanden/paginas/hoe-werkt-het.html r. 2510–2663 (+ r. 2406).
 *
 * Eén voorbeelddag, elke vijf minuten doorgerekend: woning met tien panelen, 5 kWh bruikbare
 * opslag en een dynamisch contract. De curves worden hier berekend en als pad in de SVG gezet,
 * zodat er geen handmatig ingetekende paden in de markup staan die niet meer kloppen als een
 * aanname verandert.
 *
 * De verticale as heeft bewust geen cijfers: de grafiek laat het rítme zien (wanneer vullen,
 * wanneer leeglopen), niet exacte waardes.
 *
 * Custom element <bob-day-flow> (= .day-panel), zoekt alles binnen zichzelf via data-attributen,
 * zodat het na een re-render in de theme editor (shopify:section:load) opnieuw werkt.
 * Fasenamen en -teksten komen uit Liquid (<script type="application/json" data-day-fases>);
 * de tijdgrenzen en de koppeling met de sprongknoppen horen bij het rekenmodel en staan hier vast.
 */

const STAP = 5; /* minuten per meetpunt */
const CAP = 5; /* kWh bruikbaar        */
const PIEK = 3.0; /* kWp opwek op de top  */
const OP = 375,
  ONDER = 1260; /* zonsopgang 06:15, onder 21:00 */

/* Grenzen per fase (minuten) en welke sprongknop dan oplicht. Volgorde = fase 1–6 in de editor. */
const GRENZEN = [
  { tot: 360, knop: 'nacht' },
  { tot: 540, knop: 'ochtend' },
  { tot: 960, knop: 'middag' },
  { tot: 1050, knop: 'middag' },
  { tot: 1320, knop: 'avond' },
  { tot: 1440, knop: 'nacht' },
];

/** @param {number} m */
function zon(m) {
  if (m < OP || m > ONDER) return 0;
  return PIEK * Math.pow(Math.sin((Math.PI * (m - OP)) / (ONDER - OP)), 1.6);
}
/** @param {number} m */
function huis(m) {
  let kw = m < 360 ? 0.18 : 0.28; /* sluipverbruik */
  if (m >= 420 && m < 540) kw += 0.7; /* 07:00–09:00   */
  if (m >= 1050 && m < 1320) kw += 1.25; /* 17:30–22:00   */
  return kw;
}

/* Doorrekenen: overschot gaat de accu in, tekort komt eruit, en in de
   goedkope nachturen laadt hij bij uit het net. Eén keer per pagina. */
/** @type {number[]} */
const soc = [];
{
  let s = 1.1; /* stand om 00:00 */
  const dt = STAP / 60;
  for (let m = 0; m < 1440; m += STAP) {
    const over = zon(m) - huis(m);
    if (over > 0) s += Math.min(over, 1.5) * dt;
    else s -= Math.min(-over, 0.8) * dt;
    if (m >= 120 && m < 300 && s < 4.2) s += 0.8 * dt; /* 02:00–05:00 */
    s = Math.max(0, Math.min(CAP, s));
    soc.push(s);
  }
}

/** @param {number} m */
const X = (m) => (m / 1435) * 1000;
/** @param {number} kw */
const Yv = (kw) => 220 - (kw / 3.2) * 150; /* vermogenlijnen */
/** @param {number} kwh */
const Ys = (kwh) => 220 - (kwh / CAP) * 205; /* vulling        */

/** @param {(m: number, i: number) => number} fn */
function pad(fn) {
  let d = '';
  for (let i = 0; i < soc.length; i++) {
    const m = i * STAP;
    d += (i ? ' L' : 'M') + X(m).toFixed(1) + ' ' + fn(m, i).toFixed(1);
  }
  return d;
}

class BobDayFlow extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.stilstaand = matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** @param {string} name */
    const q = (name) => /** @type {HTMLElement|null} */ (this.querySelector(`[data-${name}]`));

    this.scrub = /** @type {HTMLInputElement|null} */ (q('day-scrub'));
    if (!this.scrub) return;
    this.time = q('day-time');
    this.phase = q('day-phase');
    this.socFill = q('day-soc-fill');
    this.socVal = q('day-soc-val');
    this.marker = q('day-marker');
    this.cursor = q('day-cursor');
    this.story = q('day-story');
    this.jump = q('day-jump');

    /* Fasenamen en -teksten uit de sectie-instellingen, gekoppeld aan de vaste grenzen. */
    /** @type {{naam?: string, tekst?: string}[]} */
    let teksten = [];
    try {
      teksten = JSON.parse(q('day-fases')?.textContent || '[]');
    } catch (e) {
      teksten = [];
    }
    this.fases = GRENZEN.map((g, i) => ({
      ...g,
      naam: teksten[i]?.naam || '',
      tekst: teksten[i]?.tekst || '',
    }));

    /* Paden van de drie lijnen + het vlak onder de vulling. */
    q('day-zon-line')?.setAttribute('d', pad((m) => Yv(zon(m))));
    q('day-huis-line')?.setAttribute('d', pad((m) => Yv(huis(m))));
    const socPad = pad((m, i) => Ys(soc[i] ?? 0));
    q('day-soc-line')?.setAttribute('d', socPad);
    q('day-soc-area')?.setAttribute('d', socPad + ' L1000 220 L0 220 Z');

    /* Uurlijnen: elke drie uur een streep. De uurlabels staan in HTML
       onder de grafiek, zodat ze niet meevervormen met de opgerekte SVG. */
    const grid = q('day-grid');
    if (grid) {
      let g = '';
      for (let u = 3; u < 24; u += 3) {
        const x = X(u * 60);
        g += '<line class="day-grid-line" x1="' + x + '" y1="6" x2="' + x + '" y2="220"/>';
      }
      grid.innerHTML = g;
    }

    /* Speelt vanzelf tot je hem zelf vastpakt; daarna staat hij stil waar
       jij hem laat. Eén dag duurt ongeveer 22 seconden. */
    this.speelt = false;
    this.speelStart = 0;
    this.speelVanaf = 0;
    this.frame = 0;

    const scrub = this.scrub;
    scrub.addEventListener(
      'input',
      () => {
        this.stopSpelen();
        this.toonMoment(Number(scrub.value));
      },
      { signal }
    );
    scrub.addEventListener('pointerdown', () => this.stopSpelen(), { signal });
    scrub.addEventListener('keydown', () => this.stopSpelen(), { signal });
    this.jump?.addEventListener(
      'click',
      (e) => {
        const knop = /** @type {HTMLElement} */ (e.target).closest('button');
        if (!knop) return;
        this.stopSpelen();
        scrub.value = knop.dataset.min || '0';
        this.toonMoment(Number(knop.dataset.min));
      },
      { signal }
    );

    this.toonMoment(Number(scrub.value));

    this.observer = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries)
          if (e.isIntersecting) {
            this.startSpelen();
            obs.disconnect();
          }
      },
      { threshold: 0.45 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.stopSpelen();
  }

  /** @param {number} m */
  toonMoment(m) {
    const i = Math.min(soc.length - 1, Math.round(m / STAP));
    const uur = Math.floor(m / 60),
      min = Math.floor(m % 60);
    const klok = String(uur).padStart(2, '0') + ':' + String(min).padStart(2, '0');
    const pct = Math.round(((soc[i] ?? 0) / CAP) * 100);

    if (this.time) this.time.textContent = klok;
    this.scrub?.setAttribute('aria-valuetext', klok);
    if (this.socFill) this.socFill.style.width = pct + '%';
    if (this.socVal) this.socVal.textContent = pct + '%';

    const x = X(m);
    this.marker?.setAttribute('x1', String(x));
    this.marker?.setAttribute('x2', String(x));
    /* De stip ligt als HTML over de SVG heen: positie in procenten van het
       viewBox-raster, zodat hij precies op de oranje lijn blijft liggen. */
    if (this.cursor) {
      this.cursor.style.left = x / 10 + '%';
      this.cursor.style.top = (Ys(soc[i] ?? 0) / 250) * 100 + '%';
    }

    const fases = this.fases || [];
    const fase = fases.find((f) => m < f.tot) || fases[fases.length - 1];
    if (!fase) return;
    if (this.phase) this.phase.textContent = fase.naam;
    if (this.story && this.story.dataset.fase !== fase.tekst) {
      this.story.dataset.fase = fase.tekst;
      this.story.innerHTML = fase.tekst;
    }
    if (this.jump) {
      [...this.jump.children].forEach((b) =>
        b.classList.toggle('is-active', /** @type {HTMLElement} */ (b).dataset.knop === fase.knop)
      );
    }
  }

  stopSpelen() {
    this.speelt = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /** @param {number} nu */
  speelFrame = (nu) => {
    if (!this.speelt || !this.scrub) return;
    const m = (this.speelVanaf + ((nu - this.speelStart) / 22000) * 1440) % 1440;
    this.scrub.value = String(Math.round(m / STAP) * STAP);
    this.toonMoment(Number(this.scrub.value));
    this.frame = requestAnimationFrame(this.speelFrame);
  };

  startSpelen() {
    if (this.speelt || this.stilstaand || !this.scrub) return;
    this.speelt = true;
    this.speelVanaf = Number(this.scrub.value);
    this.speelStart = performance.now();
    this.frame = requestAnimationFrame(this.speelFrame);
  }
}

if (!customElements.get('bob-day-flow')) {
  customElements.define('bob-day-flow', BobDayFlow);
}
